import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { Button, Divider, Flex, ScrollArea } from "@mantine/core";
import { randomId } from "@mantine/hooks";
import { useCallback } from "react";

import CheckListFieldItem from "./CheckListFieldItem";

interface Props {
  items: any[];
  handlers: any;
  scrollHeight?: number;
}

const CheckListFieldArray = ({ items: fields, handlers }: Props) => {
  const handleAppend = () => {
    handlers.setState((prev: any) => [
      ...prev,
      { text: "", unit: "check", naAvailable: false, id: randomId() },
    ]);
  };

  const handleRemove = useCallback(
    (id: string) => {
      handlers.setState((prev: any[]) => prev.filter(item => item.id !== id));
    },
    [handlers],
  );

  const handleChange = useCallback(
    (index: number, val: any, prop: string) => {
      handlers.setItemProp(index, prop, val);
    },
    [handlers],
  );

  const handleDuplicate = useCallback(
    (itemToDuplicate: any) => {
      const newItem = {
        ...itemToDuplicate,
        id: randomId(),
        fields: itemToDuplicate.fields?.map((f: any) => ({
          ...f,
          id: randomId(),
        })),
      };
      handlers.setState((prev: any[]) => {
        const index = prev.findIndex(i => i.id === itemToDuplicate.id);
        const newArr = [...prev];
        newArr.splice(index + 1, 0, newItem);
        return newArr;
      });
    },
    [handlers],
  );

  const items = fields.map((item, index) => {
    if (!("naAvailable" in item)) item.naAvailable = false;
    return (
      <CheckListFieldItem
        item={item}
        key={item.id}
        index={index}
        onChange={handleChange}
        onRemove={handleRemove}
        onCopy={handleDuplicate}
      />
    );
  });
  return (
    <Flex
      direction="column"
      gap="sm"
      mt="sm"
      pb={12}
      mb={-12}
      style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Divider w="100%" mt="md" mb={0} label="Items" />
      <ScrollArea style={{ flex: 1, paddingRight: 12 }}>
        <DragDropContext
          onDragEnd={({ destination, source }) => {
            handlers.reorder({
              from: source.index,
              to: destination?.index ?? 0,
            });
          }}>
          <Droppable droppableId="dnd-list" direction="vertical">
            {provided => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {items}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </ScrollArea>
      <Button variant="outline" w="100%" onClick={handleAppend}>
        Add Check +
      </Button>
    </Flex>
  );
};

export default CheckListFieldArray;
